import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

// Setup axios interceptor to attach token from localStorage
axios.interceptors.request.use((config) => {
    const token = localStorage.getItem('vx_access_token');
    if (token && config.url?.includes('/api/')) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
            setUser(response.data);
        } catch {
            localStorage.removeItem('vx_access_token');
            localStorage.removeItem('vx_refresh_token');
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const storeTokens = (data) => {
        if (data.access_token) localStorage.setItem('vx_access_token', data.access_token);
        if (data.refresh_token) localStorage.setItem('vx_refresh_token', data.refresh_token);
    };

    const login = async (email, password) => {
        const response = await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
        storeTokens(response.data);
        setUser(response.data);
        return response.data;
    };

    const travelerAccess = async (code, email) => {
        const response = await axios.post(`${API}/api/auth/traveler/access`, { code, email }, { withCredentials: true });
        storeTokens(response.data);
        setUser(response.data);
        return response.data;
    };

    const travelerReturn = async (email, code) => {
        const response = await axios.post(`${API}/api/auth/traveler/return`, { email, code }, { withCredentials: true });
        storeTokens(response.data);
        setUser(response.data);
        return response.data;
    };

    const register = async (email, password, name, role = 'operator') => {
        const response = await axios.post(`${API}/api/auth/register`, { email, password, name, role }, { withCredentials: true });
        storeTokens(response.data);
        setUser(response.data);
        return response.data;
    };

    const logout = async () => {
        try { await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true }); } catch {}
        localStorage.removeItem('vx_access_token');
        localStorage.removeItem('vx_refresh_token');
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth, travelerAccess, travelerReturn }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}
